import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { useMutationApi, useQueryApi } from "api/hooks.ts";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "types/models.ts";

import { labelsApi } from "./api";

import GetLabelsResponse = App.Http.Responses.Label.GetLabelsResponse;
import GetLabelResponse = App.Http.Responses.Label.GetLabelResponse;
import GetLabelsRequest = App.Http.Requests.Label.GetLabelsRequest;
import GetLabelRequest = App.Http.Requests.Label.GetLabelRequest;
import StoreLabelRequest = App.Http.Requests.Label.StoreLabelRequest;
import UpdateLabelRequest = App.Http.Requests.Label.UpdateLabelRequest;

export const useGetLabelsQuery = (
    request: GetLabelsRequest,
    options: Omit<UseQueryOptions<GetLabelsResponse, AxiosError<ApiErrorResponse>, GetLabelsResponse>, "queryKey">
) => {
    return useQueryApi<GetLabelsResponse>({
        ...options,
        queryKey: ["getLabels"],
        queryFn: () => labelsApi.index(request),
    });
};

export const useGetLabelQuery = (
    labelId: string,
    request: GetLabelRequest,
    options?: Omit<UseQueryOptions<GetLabelResponse, AxiosError<ApiErrorResponse>>, "queryKey">
) => {
    return useQueryApi<GetLabelResponse>({
        queryKey: ["getLabel", labelId],
        queryFn: () => labelsApi.show(labelId, request),
        ...options,
    });
};

export const useStoreLabelMutation = (
    options: UseMutationOptions<GetLabelResponse, AxiosError<ApiErrorResponse>, StoreLabelRequest> = {}
) => {
    return useMutationApi<GetLabelResponse, StoreLabelRequest>({
        mutationKey: ["storeLabel"],
        mutationFn: (request: StoreLabelRequest) => labelsApi.store(request),
        ...options,
    });
};

export const useUpdateLabelMutation = (
    options: UseMutationOptions<
        GetLabelResponse,
        AxiosError<ApiErrorResponse>,
        { labelId: string; request: UpdateLabelRequest }
    > = {}
) => {
    return useMutationApi<GetLabelResponse, { labelId: string; request: UpdateLabelRequest }>({
        mutationKey: ["updateLabel"],
        mutationFn: ({ labelId, request }) => labelsApi.update(labelId, request),
        ...options,
    });
};

export const useDestroyLabelMutation = (
    options: UseMutationOptions<GetLabelResponse, AxiosError<ApiErrorResponse>, string> = {}
) => {
    return useMutationApi<GetLabelResponse, string>({
        mutationKey: ["destroyLabel"],
        mutationFn: (labelId: string) => labelsApi.destroy(labelId),
        ...options,
    });
};
